'use client'
import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Typography, Box, Grid } from '@mui/material';
import StandardModal from "@/components/modals/StandardModal";
import ConfirmationModal from "@/components/modals/ConfirmationModal";
import TextField from '@mui/material/TextField';
import { GOA_EVENT_ENTRY_TYPE } from '@/stack-shared/interfaces/model/goa-event.interface';
import { SPACING } from '@/app/constants/layout-constants';
import { useTodayLocationVisit } from '@/hooks/api/useLocationVisits';
import GenericActionPicker, { GENERIC_ICON_NAME } from '../form/GenericActionPicker';
import { StandardUploadInput } from '@/components/form/StandardUploadInput';
import { RESOURCE_TYPE, UPLOAD_COLLECTION_METHOD } from '@/stack-shared/constants/resource-constants';
import { getFileExtension } from '@/utils/general';
import { useUploadResourceFile } from '@/hooks/api/useResources';
import { useNotification } from '@/contexts/NotificationContext';
import { useUpsertGoaEvents } from '@/hooks/useLocation';
import { useGoaImageAI } from '@/hooks/api/useGoas';
import FullScreenLoader from '@/components/shared/FullScreenLoader';
import { cloneDeep } from 'lodash';


export interface IGoaValue {
  hardIn: number;
  hardOut: number;
  lastHardIn: number;
  lastHardOut: number;
  locationId: number | undefined;
  assetId: string;
  goaId: number | undefined;
  position?: number | null;
  event: GOA_EVENT_ENTRY_TYPE;
  serviceable: boolean;
  serviceIssue: string;
}

type FileState = {
  file: File;
  changed: boolean;
};

interface ILocationVisitGoaFormProps {
  goaValues: IGoaValue[];
  setGoaValues: (value: IGoaValue[]) => void;
  handleDataRefresh: () => void;
  openGoaModal: boolean;
  setOpenGoaModal: (value: boolean) => void;
}

  const LocationVisitGoaForm: React.FC<ILocationVisitGoaFormProps> = ({ goaValues, setGoaValues, handleDataRefresh, openGoaModal, setOpenGoaModal }) => {

  const [showFullScreenLoader, setShowFullScreenLoader] = useState(false);
  const [aiGoaValues, setAiGoaValues] = useState<IGoaValue[]>(cloneDeep(goaValues));
  const [warningIndexes, setWarningIndexes] = useState<number[]>([]);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [showAiValues, setShowAiValues] = useState(false);
  const [goaValuesToUpdate, setGoaValuesToUpdate] = useState<IGoaValue[]>([]);
  
  // Local state for form inputs to prevent re-renders
  const [localInputValues, setLocalInputValues] = useState<Record<string, { hardIn: string; hardOut: string; serviceIssue: string }>>({});
  const prevLocalInputValuesRef = useRef<Record<string, { hardIn: string; hardOut: string; serviceIssue: string }>>({});
  
  const upsertGoaEventsMutation = useUpsertGoaEvents();
  const uploadResourceFileMutation = useUploadResourceFile();
  const goaImageAIMutation = useGoaImageAI();
  const { showSuccess, showError } = useNotification();
  const { data: todayVisitData } = useTodayLocationVisit();

  // Initialize local input values when goaValues change
  useEffect(() => {
    console.log("=== LocationVisitGoaForm useEffect DEBUG ===");
    console.log("goaValues:", goaValues);
    console.log("prevLocalInputValuesRef.current:", prevLocalInputValuesRef.current);
    
          const initialValues: Record<string, { hardIn: string; hardOut: string; serviceIssue: string }> = {};
      goaValues.forEach((goa, index) => {
        // Preserve existing values if they exist, otherwise use empty strings
        const existingValues = prevLocalInputValuesRef.current[index] || { hardIn: '', hardOut: '', serviceIssue: '' };
        
        console.log(`GOA ${goa.assetId}: goa.hardIn=${goa.hardIn}, existingValues.hardIn='${existingValues.hardIn}'`);
        
        initialValues[index] = {
          hardIn: existingValues.hardIn || (goa.hardIn > 0 ? goa.hardIn.toString() : ''),
          hardOut: existingValues.hardOut || (goa.hardOut > 0 ? goa.hardOut.toString() : ''),
          serviceIssue: goa.serviceIssue || existingValues.serviceIssue
        };
        
        console.log(`GOA ${goa.assetId}: final hardIn='${initialValues[index].hardIn}'`);
      });
    setLocalInputValues(initialValues);
  }, [goaValues]);
  
  // Update ref when localInputValues changes
  useEffect(() => {
    prevLocalInputValuesRef.current = localInputValues;
  }, [localInputValues]);

  // Optimized input change handler using local state
  const handleInputChange = useCallback((index: number, field: 'hardIn' | 'hardOut' | 'serviceIssue', value: string) => {
    setLocalInputValues(prev => ({
      ...prev,
      [index]: {
        ...prev[index],
        [field]: value
      }
    }));
  }, []);

  const handleSubmitImage = (selectedFile: File, goaId: number, type: string) => {
    setShowFullScreenLoader(true);
    try {
      getFileExtension(selectedFile!.name);
    } catch (e) {
      showError('Invalid File Extension.');
      return;
    }

    const uploadData = {
      name: type,
      model: 'goa',
      modelId: goaId,
      secondaryModel: 'location-visit',
      secondaryModelId: todayVisit?.id,
      context: type,
      extension: getFileExtension(selectedFile!.name),
      fileName: `${type}-${goaId}.${getFileExtension(selectedFile!.name)}`,
      originalFileName: selectedFile!.name,
      uploadCollectionMethod: UPLOAD_COLLECTION_METHOD.REPLACE,
      type: RESOURCE_TYPE.IMAGE,
      parts: 1,
      file: selectedFile,
      rootPath: 'location-visit/' + todayVisit?.id,
      primary: true,
      conversions: [],
      fileSize: selectedFile!.size
    };

    dispatch(
      uploadResourceFileAction(
        uploadData,
        (data: any) => {
          enqueueSnackbar(`File uploaded.`, { variant: 'success' });
          dispatch(goaImageAI({ fileName: `${type}-${goaId}.${getFileExtension(selectedFile!.name)}`, goaId, locationVisitId: todayVisit?.id, type }, (data) => {
            console.log("raw response", data)
            const goaValuesClone = cloneDeep(goaValues);
            let doDataRefresh = false;
            if (data.in.status === "set-to-ai") {
              const newGoaValues = goaValuesClone.map((goa) => {
                if (goa.goaId === goaId) {
                  goa.hardIn = data.goaEvent.hardIn;
                }
                return goa;
              });
              setAiGoaValues(newGoaValues);
              //enqueueSnackbar(`In value set.`, { variant: 'success' });
              // doDataRefresh = true;
            }
            else if (data.in.status === "confirmed") {
              enqueueSnackbar(`In value confirmed.`, { variant: 'success' });
            }
            else if (data.in.status === "mismatch") {
              enqueueSnackbar(`In value mismatch.`, { variant: 'error' });
            }
            if (data.out.status === "set-to-ai") {
              //enqueueSnackbar(`Out value set.`, { variant: 'success' });
              const newGoaValues = goaValuesClone.map((goa) => {
                if (goa.goaId === goaId) {
                  goa.hardOut = data.goaEvent.hardOut;
                }
                return goa;
              });
              setAiGoaValues(newGoaValues);
              // doDataRefresh = true;
            }
            else if (data.out.status === "confirmed") {
              enqueueSnackbar(`Out value confirmed.`, { variant: 'success' });
            }
            else if (data.out.status === "mismatch") {
              enqueueSnackbar(`Out value mismatch.`, { variant: 'error' });
            }
            if (doDataRefresh) {
              handleDataRefresh();
            }
            setShowFullScreenLoader(false);
          }));
        },
        () => {
          setShowFullScreenLoader(false);
          enqueueSnackbar(`File upload failed.`, { variant: 'error' });
        },
      ),
    );
  };

  const { locationId } = useParams();

  const [blackBoxFiles, setBlackBoxFiles] = useState<Record<number, FileState>>({});
  const [gameAccountingFiles, setGameAccountingFiles] = useState<Record<number, FileState>>({});
  
  const todayVisit = todayVisitData && locationId && Number(todayVisitData.locationId) === Number(locationId) ? todayVisitData : null;

  //console.log("todayVisit?", todayVisit);

  const getActions = (goa: IGoaValue) => {
    let returnActions = [];
    if (goa.serviceable) {
      returnActions.push(
        {
          label: "Unserviceable",
          icon: GENERIC_ICON_NAME.BLOCK,
          onClick: (row: any) => {
            const goaValuesUpdate = [...goaValues] as any;
            const rowIndex = goaValuesUpdate.findIndex((x: any) => x.assetId === row.assetId);
            goaValuesUpdate[rowIndex].serviceable = false;
            goaValuesUpdate[rowIndex].event = GOA_EVENT_ENTRY_TYPE.UNSERVICEABLE;
            setGoaValues(goaValuesUpdate);
          },
        },
      )
    }
    else {
      returnActions.push(
        {
          label: "Serviceable",
          icon: GENERIC_ICON_NAME.CHECK_CIRCLE,
          onClick: (row: any) => {
            const goaValuesUpdate = [...goaValues] as any;
            const rowIndex = goaValuesUpdate.findIndex((x: any) => x.assetId === row.assetId);
            goaValuesUpdate[rowIndex].serviceable = true;
            goaValuesUpdate[rowIndex].event = GOA_EVENT_ENTRY_TYPE.CASH_PULL;
            setGoaValues(goaValuesUpdate);
          },
        },
      )
    }
    return returnActions;
  }

  return (
    <StandardModal
      title="Service Goas"
      open={openGoaModal}
      enableAutoFullscreen={true}
      maxWidth="xl"
      fullScreen={true}
      disableBackdropClick={true}
      disableEscapeKeyDown={true}
      handleClose={() => setOpenGoaModal(false)}
      action={async () => {
        console.log("Starting submission...");
        setWarningIndexes([]);
        let showWarning = false;
        const updatedGoaValues = await Promise.all(goaValues.map(async (goa, index) => {
          const localValues = localInputValues[index] || { hardIn: '', hardOut: '', serviceIssue: '' };
          const hardIn = parseFloat(localValues.hardIn) || 0;
          const hardOut = parseFloat(localValues.hardOut) || 0;
          console.log(`GOA ${index} values:`, { hardIn, hardOut, aiValues: aiGoaValues[index] });

          if (aiGoaValues[index] && (hardIn !== aiGoaValues[index]?.hardIn || hardOut !== aiGoaValues[index]?.hardOut)) {
            setWarningIndexes([...warningIndexes, index]);
            showWarning = true;
          }

          return {
            ...goa,
            hardIn,
            hardOut,
            serviceIssue: !goa.serviceable ? localValues.serviceIssue : ""
          };
        }));

        console.log("Updated GOA values:", updatedGoaValues);
        console.log("Show warning:", showWarning);

        if (showWarning) {
          setGoaValuesToUpdate(updatedGoaValues);
          setShowWarningModal(true);
          return;
        }

        const dispatchRequest: any = {
          goas: updatedGoaValues,
        };

        if (todayVisit?.id) {
          dispatchRequest.locationVisitId = todayVisit.id;
        }

        console.log("Dispatching request:", dispatchRequest);
        upsertGoaEventsMutation.mutate(dispatchRequest, {
          onSuccess: (data) => {
            console.log("Dispatch callback data:", data);
            handleDataRefresh();
          },
          onError: (error: any) => {
            showError(error.message || 'Failed to update GOA events');
          },
        });

        setOpenGoaModal(false);
      }}

    >
      <Box style={{ paddingTop: SPACING.md }}>

        <FullScreenLoader open={showFullScreenLoader} />

        <Grid
          container
          spacing={2}
          sx={{
            padding: 1,
            borderBottom: "2px solid #000",
            fontWeight: "bold",
            backgroundColor: "#f5f5f5",
            marginBottom: 1,
            display: {
              xs: 'none', // visible on extra-small screens
              sm: 'flex'   // hidden on small screens and above
            }
          }}
        >
          <Grid size={2}>
            <Typography variant="subtitle1">Order - Asset ID</Typography>
          </Grid>
          <Grid size={3}>
            <Typography variant="subtitle1">Hard In</Typography>
          </Grid>
          <Grid size={3}>
            <Typography variant="subtitle1">Hard Out</Typography>
          </Grid>
          <Grid size={1}>
            <Typography variant="subtitle1">B. Box</Typography>
          </Grid>
          <Grid size={1}>
            <Typography variant="subtitle1">Acct.</Typography>
          </Grid>
          <Grid size={2}>
            <Typography variant="subtitle1">Actions</Typography>
          </Grid>
        </Grid>



        {goaValues.map((goa, index) => {
          const localValues = localInputValues[index] || { hardIn: '', hardOut: '', serviceIssue: '' };
          
          return (
            <Grid
              container
              spacing={2}
              key={index}
              mb={0}
              sx={{
                padding: 1,
                borderBottom: "1px solid #e0e0e0",
              }}
            >
              <Grid size={{
                xs: 4,
                sm: 2
              }} sx={goa.serviceable === false ? { opacity: 0.5, pointerEvents: "none" } : {}}>
                <Typography
                  variant="caption"
                  sx={{
                    display: {
                      xs: 'block', // visible on extra-small screens
                      sm: 'none'   // hidden on small screens and above
                    }
                  }}
                >Order - Asset ID</Typography>
                <Typography variant="h6">
                  {goa.position != null ? `${goa.position} - ${goa.assetId}` : goa.assetId}
                </Typography>
              </Grid>
              <Grid size={{
                xs: 4,
                sm: 3
              }} sx={goa.serviceable === false ? { opacity: 0.5, pointerEvents: "none" } : {}}>
                <TextField
                  type="number"
                  id={`hardIn-${index}`}
                  label={goa.lastHardIn ? `HardIn (${goa.lastHardIn})` : "HardIn"}
                  variant="outlined"
                  value={localValues.hardIn}
                  onChange={(e) => handleInputChange(index, 'hardIn', e.target.value)}
                  onBlur={(e) => {
                    // Keep empty string if user clears the field
                    if (e.target.value === '') {
                      handleInputChange(index, 'hardIn', '');
                    }
                  }}
                  disabled={!goa.serviceable}
                />
                <br/>
                {
                  !!(showAiValues && aiGoaValues[index] && aiGoaValues[index].hardIn && aiGoaValues[index].hardIn !== parseFloat(localValues.hardIn)) && (
                    <Typography variant="caption" color="warning" fontWeight={"bold"}>
                      Resource value: {aiGoaValues[index].hardIn}
                    </Typography>
                  )
                }
              </Grid>
              <Grid size={{
                xs: 4,
                sm: 3
              }} sx={goa.serviceable === false ? { opacity: 0.5, pointerEvents: "none" } : {}}>
                <TextField
                  type="number"
                  id={`hardOut-${index}`}
                  label={goa.lastHardOut ? `HardOut (${goa.lastHardOut})` : "HardOut"}
                  variant="outlined"
                  value={localValues.hardOut}
                  onChange={(e) => handleInputChange(index, 'hardOut', e.target.value)}
                  onBlur={(e) => {
                    // Keep empty string if user clears the field
                    if (e.target.value === '') {
                      handleInputChange(index, 'hardOut', '');
                    }
                  }}
                  disabled={!goa.serviceable}
                />
                <br/>
                {
                  !!(showAiValues && aiGoaValues[index] && aiGoaValues[index].hardOut && aiGoaValues[index].hardOut !== parseFloat(localValues.hardOut)) && (
                    <Typography variant="caption" color="warning" fontWeight={"bold"}>
                      Resource value: {aiGoaValues[index].hardOut}
                    </Typography>
                  )
                }
              </Grid>

              <Grid size={{
                xs: 4,
                sm: 1
              }}>
                <Typography
                  variant="caption"
                  sx={{
                    display: {
                      xs: 'block', // visible on extra-small screens
                      sm: 'none'   // hidden on small screens and above
                    }
                  }}
                >B. Box</Typography>
                <StandardUploadInput
                  showPreview={true}
                  onFileSelected={(files: any) => {
                    setBlackBoxFiles(prev => ({
                      ...prev,
                      [goa.goaId!]: { file: files[0], changed: true }
                    }));
                    handleSubmitImage(files[0], goa.goaId!, "black-box");
                  }}
                  handleSubmit={(file: any) => console.log(file)}
                  initialImage={resources.find((x) => 
                    x.modelId === goa.goaId && 
                    x.context === "black-box" &&
                    (!todayVisit?.id || x.secondaryModelId === todayVisit.id)
                  )?.signedUrl}
                  accept={'image/*'}
                  uploadType={RESOURCE_TYPE.IMAGE}
                  isThumbnail={true}
                  previewImageStyles={{
                    width: 60,
                    height: 60
                  }}
                />
              </Grid>
              <Grid size={{
                xs: 4,
                sm: 1
              }}>
                <Typography
                  variant="caption"
                  sx={{
                    display: {
                      xs: 'block', // visible on extra-small screens
                      sm: 'none'   // hidden on small screens and above
                    }
                  }}
                >Acct</Typography>
                <StandardUploadInput
                  showPreview={true}
                  onFileSelected={(files: any) => {
                    setGameAccountingFiles(prev => ({
                      ...prev,
                      [goa.goaId!]: { file: files[0], changed: true }
                    }));
                    handleSubmitImage(files[0], goa.goaId!, "game-accounting");
                  }}
                  handleSubmit={(file: any) => console.log(file)}
                  initialImage={resources.find((x) => 
                    x.modelId === goa.goaId && 
                    x.context === "game-accounting" &&
                    (!todayVisit?.id || x.secondaryModelId === todayVisit.id)
                  )?.signedUrl}
                  accept={'image/*'}
                  uploadType={RESOURCE_TYPE.IMAGE}
                  isThumbnail={true}
                  previewImageStyles={{
                    width: 60,
                    height: 60
                  }}
                />
              </Grid>
              <Grid size={{
                xs: 4,
                sm: 2
              }}>
                <Typography
                  variant="caption"
                  sx={{
                    display: {
                      xs: 'block', // visible on extra-small screens
                      sm: 'none'   // hidden on small screens and above
                    }
                  }}
                >Actions</Typography>
                <GenericActionPicker row={goa} actions={getActions(goa)} />
              </Grid>
              {goa.serviceable === false && <Grid size={12}>
                <TextField
                  type="text"
                  fullWidth
                  id={`serviceIssue-${index}`}
                  label={"Service Issue"}
                  variant="outlined"
                  value={localValues.serviceIssue}
                  onChange={(e) => handleInputChange(index, 'serviceIssue', e.target.value)}
                  disabled={goa.serviceable}
                />
              </Grid>}
            </Grid>
          );
        })}
      </Box>

      <ConfirmationModal
        open={showWarningModal}
        message="Looks like the values you entered differ from the pictures. Are you sure you want to proceed?"
        onClose={() => {
          setShowAiValues(true);
          setShowWarningModal(false);
        }}
        onConfirm={() => {
          setShowWarningModal(false);
          const dispatchRequest: any = {
            goas: goaValuesToUpdate,
          };
          if (todayVisit?.id) {
            dispatchRequest.locationVisitId = todayVisit.id;
          }
          upsertGoaEventsMutation.mutate(dispatchRequest, {
            onSuccess: () => {
              handleDataRefresh();
            },
            onError: (error: any) => {
              showError(error.message || 'Failed to update GOA events');
            },
          });
          setOpenGoaModal(false);
        }}
      />

    </StandardModal>
  )
}

export default LocationVisitGoaForm;

